import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { apiClient } from '@/shared/utils/api';
import authService from '@/domains/internal/auth/services/authService';
import { Button } from '@/shared/components/ui';
import { BACKEND_CONFIG } from '@/config/backendConfig';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validatePasswords = () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validatePasswords()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Use backend-aware endpoint
      const changePasswordEndpoint = BACKEND_CONFIG.isFastAPI
        ? '/api/internal/auth/change-password'
        : '/auth/change-password';

      const response = await apiClient.post(changePasswordEndpoint, {
        current_password: currentPassword,
        new_password: newPassword
      });

      // Django returns new tokens, FastAPI just returns success message
      if (response.data.access_token && response.data.refresh_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      // Clear the must_change_password flag
      localStorage.removeItem('must_change_password');

      // Password changed successfully, redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Failed to change password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-2xl font-bold text-[rgba(47,43,61,0.9)] mb-2">
            Change Your Password
          </h1>
          <p className="text-[rgba(47,43,61,0.7)]">
            You must change your password before continuing.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-[rgba(47,43,61,0.9)] mb-2">
              Current Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(47,43,61,0.5)] w-5 h-5" />
              <input
                id="currentPassword"
                name="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-[var(--color-border)] rounded-lg text-[17px] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-light)] transition-all"
                placeholder="Enter current password"
                disabled={isSubmitting}
                style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}
              />
              <Button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                variant="neutral"
                padding="p-0"
                className="!bg-transparent absolute right-3 top-1/2 transform -translate-y-1/2 !text-[rgba(47,43,61,0.5)] hover:!text-[rgba(47,43,61,0.9)]"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-[rgba(47,43,61,0.9)] mb-2">
              New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(47,43,61,0.5)] w-5 h-5" />
              <input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-[var(--color-border)] rounded-lg text-[17px] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-light)] transition-all"
                placeholder="Enter new password"
                disabled={isSubmitting}
                style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}
              />
              <Button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                variant="neutral"
                padding="p-0"
                className="!bg-transparent absolute right-3 top-1/2 transform -translate-y-1/2 !text-[rgba(47,43,61,0.5)] hover:!text-[rgba(47,43,61,0.9)]"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </Button>
            </div>
            <p className="text-xs text-[rgba(47,43,61,0.6)] mt-1">
              Must be at least 8 characters long
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-[rgba(47,43,61,0.9)] mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(47,43,61,0.5)] w-5 h-5" />
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-[var(--color-border)] rounded-lg text-[17px] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-light)] transition-all"
                placeholder="Confirm new password"
                disabled={isSubmitting}
                style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}
              />
              <Button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                variant="neutral"
                padding="p-0"
                className="!bg-transparent absolute right-3 top-1/2 transform -translate-y-1/2 !text-[rgba(47,43,61,0.5)] hover:!text-[rgba(47,43,61,0.9)]"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="primary"
              padding="px-4 py-3"
              className="flex-1"
            >
              {isSubmitting ? 'Changing Password...' : 'Change Password'}
            </Button>
            <Button
              type="button"
              onClick={handleLogout}
              disabled={isSubmitting}
              variant="neutral"
              padding="px-4 py-3"
              className="!border !border-[var(--color-border)] !text-[rgba(47,43,61,0.9)] hover:!bg-gray-50"
            >
              Logout
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
